// @flow

import { useTranslation } from "react-i18next";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { SchemaTree, SchemaTreeType } from "../../../types/SchemaTree";
import Tooltip from "@kea-mod/common/tooltip";
import { KeyValueEntry } from "../../../types/KeyValueEntry";
import { ApiContext } from "../../../context/KeaAPI/newApi";

type Props = { parentSchemaId: number; closeAction: () => void; visible: boolean };

/**
 * This component is used to add a grading service to a schema.
 * It is shown inline in the tree view.
 */
export const TreeViewAddGradingServiceComponent = (props: Props) => {
  type State = {
    gradingServiceId: number | undefined;
    scenario: boolean;
    modelingLanguage: boolean;
    interactionLog: boolean;
    weight: string;
    keyValueEntries: KeyValueEntry[];
  };

  const { t } = useTranslation();
  const { schemaApi, schemaTreeApi, gradingServiceApi } = useContext(ApiContext);

  const parentSchema = schemaApi.elements.find((schema) => schema.id === props.parentSchemaId);
  if (!parentSchema)
    throw Error("Unknown schema with id " + props.parentSchemaId + " in TreeViewAddGradingServiceComponent.");

  const [gradingServiceEdgeCreated, setGradingServiceEdgeCreated] = useState(false);
  const defaultState: State = {
    // can be used to reset.
    gradingServiceId: undefined,
    scenario: true,
    modelingLanguage: true,
    interactionLog: true,
    weight: "100",
    keyValueEntries: [],
  };
  const [state, setState] = useState<State>(defaultState);

  const reset = () => {
    setState(defaultState);
    setGradingServiceEdgeCreated(false);
  };

  // every time this component is shown, reset the form
  useEffect(() => {
    if (props.visible) reset();
  }, [props.visible]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const handleCheckboxChange = (event: any) => {
    setState({ ...state, [event.target.name]: event.target.checked });
  };

  const handleSelectChange = (event: any) => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  /**
   * Creates a grading service edge in the database.
   */
  const createGradingService = async () => {
    const weightInt = parseInt(state.weight, 10);

    if (gradingServiceEdgeCreated) {
      alert(t("alreadyCreatingGradingServiceEdge"));
      return;
    } else if ([state.gradingServiceId, state.weight.trim()].some((i) => !i)) {
      alert(t("fillAllFields"));
      return;
    } else if (!(0 <= weightInt && weightInt <= 100)) {
      // negative or
      alert(t("weightBetween0And100"));
      return;
    }

    // create edge. id, createdAt, updatedAt are set by the server and discarded by the createSchemaTree function.
    let newEdge: SchemaTree = {
      id: -1,
      type: SchemaTreeType.GRADING_SERVICE,
      childId: state.gradingServiceId!!,
      parentId: props.parentSchemaId,
      weight: parseInt(state.weight, 10),
      scenario: state.scenario,
      language: state.modelingLanguage,
      interactionData: state.interactionLog,
      createdAt: "",
      updatedAt: "",
    };

    let edgeID = await schemaTreeApi.create(newEdge);
    console.log("Edge (schema tree) created with ID: " + edgeID);

    setGradingServiceEdgeCreated(true);
    // Not sure if it is possible to hit the button between this and the next line. But since both setXXX-functions
    // are async, it doesn't hurt track the state of the edge creation.
    reset();
    props.closeAction();
  };

  return (
    <div className="container mt-6" style={{ display: props.visible ? undefined : "none" }}>
      <div className="section">
        <div className="columns is-centered">
          <div className="column is-three-quarters">
            <div className="box">
              <h4 className="title is-4">{t("addchildgradingservice")}</h4>

              {/* Grading service selection page. */}
              <div className="field">
                <label className="label" htmlFor="title">
                  {t("gradingservice")}
                </label>
                <div className="select">
                  <select
                    value={state.gradingServiceId}
                    onChange={handleSelectChange}
                    id="gradingServiceId"
                    name="gradingServiceId"
                  >
                    {<option value={undefined}>{t("selectGradingService")}</option>}
                    {gradingServiceApi.elements.map((gs) => (
                      <option key={gs.id} id={gs.id.toString()} value={gs.id}>
                        {gs.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/*Boolean fields*/}
              <div className="field">
                <div className="label">{t("scenario")}</div>
                <input name="scenario" type="checkbox" checked={state.scenario ?? ""} onChange={handleCheckboxChange} />
              </div>
              <div className="field">
                <div className="label">{t("modelingLanguage")}</div>
                <input
                  name="modelingLanguage"
                  type="checkbox"
                  checked={state.modelingLanguage ?? ""}
                  onChange={handleCheckboxChange}
                />
              </div>
              <div className="field">
                <div className="label">{t("interactionLog")}</div>
                <input
                  name="interactionLog"
                  type="checkbox"
                  checked={state.interactionLog ?? ""}
                  onChange={handleCheckboxChange}
                />
              </div>

              {/*Weight*/}
              <div className="field">
                <label className="label">
                  {t("weight")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("weightTooltip")}
                  ></Tooltip>
                </label>
                <input
                  className="input"
                  type="text"
                  name="weight"
                  value={state.weight ?? ""}
                  onChange={handleInputChange}
                />
              </div>

              {/*Buttons*/}
              <div className="columns mt-4">
                <button
                  className="button is-danger"
                  onClick={() => {
                    reset();
                    props.closeAction();
                  }}
                >
                  {t("cancel")}
                </button>
                <button className="button is-normal is-success" onClick={createGradingService}>
                  {t("create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
